import { differenceBy } from "lodash-es";
import { uniq } from "lodash-es";
import { Just, type Maybe, Nothing } from "purify-ts";
import { match } from "ts-pattern";
import type { ZodType, ZodTypeDef } from "zod";

import { createErrorHandler, type ErrorHandlerProps } from "@/app-utils/errors.ts";
import {
  getIconElementForCustomPlaybook,
  type ICombinedRegistrationNgsPlaybook,
  type ICombinedRegistrationNgsPlaybookConfig,
  type IConfiguredAttribute,
  type ISampleRegistrationPlaybookConfig,
} from "@/app-utils/playbook.ts";
import { type ProgressCallback } from "@/app-utils/upload.ts";
import {
  type ActivityExtendedInstance,
  type ActivityParameter,
  type GenomeViewerUiContext,
  type InputActivityExtendedInstanceInput,
  type InputActivityExtendedParameterInput,
  type InputPlaybookParametersUiWrapperInput,
  type InputSampleValueInput,
  WorkflowParameterTypeEnum,
} from "@/graphql.ts";
import { UiContextEnum } from "@/graphql.ts";
import {
  type ActivityInstanceExtendedParameter,
  type ExperimentInput,
  type InputExperimentInput,
  type InputNfParamInput,
  type InputNfParamValueInput,
  type InputNfPlaybookActivity,
  type InputNfPlaybookConfigInput,
  type InputNfTrackerParamInput,
  type InputP2PlaybookConfigInput,
  type InputPlaybookCategoryInput,
  type InputPlaybookInput,
  type InputWorkflowExportMapping,
  type InputWorkflowParamAttributeMapping,
  type NfPlaybookActivity,
  type NfSampleAttribute,
  type NfWorkflow,
  type Playbook,
  type PlaybookCategory,
  type ReportTemplateResources,
  type Workflow,
  type WorkflowP2,
} from "@/graphql.ts";
import { selMessage } from "@/messages/message.ts";
import type {
  BooleanParameterValueFragment,
  FileParameterValueFragment,
  GenomeParameterValueFragment,
  NumberParameterValueFragment,
  RegionParameterValueFragment,
  SampleParameterValueFragment,
  StringParameterValueFragment,
} from "@/modules/workflow/graphql/workflow.gql.ts";
import { extractValue } from "@/modules/workflow/utils/generateWorkflow.ts";
import { getSampleFileMappings } from "@/modules/workflow/utils/sampleFileMappings.ts";
import type {
  AllParametersTypes,
  RegionParamType,
  WorkflowActivityParameterFieldProps,
  WorkflowParameter,
} from "@/modules/workflow/utils/types.ts";
import { generateValidationSchema } from "@/modules/workflow/utils/validationSchema.ts";
import { type SelectorIcon } from "@/settings/assets-settings.ts";
import { PLAYBOOK_REPORT_TEMPLATE_RESOURCES, PLAYBOOK_TYPE } from "@/settings/settings.ts";

import type WebApiClient from "../../api/WebApiClient.js";
import { buildSavedPlaybookMessage } from "../../app-utils/alert.js";
import { SelMessage } from "../../messages/SelMessage.js";
import { notify } from "../../modules/notifications/notify.js";
import { type NfParamConfiguration } from "../../ui-modules/playbooks/editor/NfParamsConfigurator.js";
import {
  CLEAR_PLAYBOOK_CONFIG_STATE,
  DELETE_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
  DELETE_ADMIN_PLAYBOOK_CATEGORIES_REQUEST,
  DELETE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
  DOWNLOAD_FILE_FAILED,
  EDIT_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
  EDIT_ADMIN_PLAYBOOK_CATEGORIES_REQUEST,
  EDIT_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
  LOAD_ADMIN_NFWORKFLOW_FAILURE,
  LOAD_ADMIN_NFWORKFLOW_REQUEST,
  LOAD_ADMIN_P2WORKFLOW_FAILURE,
  LOAD_ADMIN_P2WORKFLOW_REQUEST,
  LOAD_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
  LOAD_ADMIN_PLAYBOOK_CATEGORIES_REQUEST,
  LOAD_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
  LOAD_ADMIN_PLAYBOOK_FAILURE,
  LOAD_ADMIN_PLAYBOOK_REQUEST,
  LOAD_ADMIN_PLAYBOOK_SUCCESS,
  LOAD_ADMIN_PLAYBOOKS_FAILURE,
  LOAD_ADMIN_PLAYBOOKS_REQUEST,
  LOAD_ADMIN_PLAYBOOKS_SUCCESS,
  LOAD_APPROVED_WORKFLOWS_FAILURE,
  LOAD_APPROVED_WORKFLOWS_REQUEST,
  LOAD_PLAYBOOK_CATEGORY_FAILURE,
  LOAD_PLAYBOOK_CATEGORY_REQUEST,
  LOAD_PLAYBOOK_CATEGORY_SUCCESS,
  SAVE_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
  SAVE_ADMIN_PLAYBOOK_CATEGORIES_REQUEST,
  SAVE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
  SAVE_PLAYBOOK_FAILURE,
  SAVE_PLAYBOOK_REQUEST,
  SAVE_PLAYBOOK_SUCCESS,
  UPDATE_PLAYBOOK_CONFIG_STATE,
  UPLOAD_FILE_FAILED,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";

/**
 * State definition
 */

export interface IP2ContextParams {
  genomeParam: ActivityInstanceExtendedParameterWithActivity;
  regionParam: ActivityInstanceExtendedParameterWithActivity;
}

export interface ActivityInstanceExtendedParameterWithActivity extends ActivityInstanceExtendedParameter {
  activity: ActivityExtendedInstance;
  // The workflowParameter is used to deal with the setting and validation of the default value during playbook config
  // It holds the "current" value, which is derived right after loading from the extended parameter. After editing of the value,
  // the value in workflowParameter is updated.
  workflowParameter?: WorkflowParameter;
}

export interface IPlaybookAdminState {
  // administration of playbooks
  adminPlaybooks: Playbook[];
  isLoadingAdminPlaybooks: boolean;
  playbookConfigState: Partial<IPlaybookConfigState>; // holds the state of the playbook configuration for creating/editing

  // admin of playbook categories
  adminPlaybookCategories: PlaybookCategory[];
  isLoadingPlaybookCategories: boolean;
}

const initialState: IPlaybookAdminState = {
  adminPlaybooks: [],
  isLoadingAdminPlaybooks: false,
  adminPlaybookCategories: [],
  isLoadingPlaybookCategories: false,
  playbookConfigState: {},
};

export interface IPlaybookConfigState {
  initialPlaybook: Playbook; // in editing mode, e.g. useful for async name validation
  nfWorkflow?: NfWorkflow; // hold all the Nextflow workflow metadata to pick from
  id: number; // editing mode
  name: string;
  nameSuffix: string;
  description: string;
  playbookCategories: PlaybookCategory[];
  uiContexts: string[];
  icon: SelectorIcon;
  sampleTypeId: number;
  playbookType: PLAYBOOK_TYPE;
  workflowId?: string | number;
  workflowImportMappings?: InputWorkflowParamAttributeMapping[];
  workflowExportMappings?: InputWorkflowExportMapping[];
  parentAttributes: Partial<IConfiguredAttribute>[];
  workflowAttributes: Partial<IConfiguredAttribute>[];
  nfParams?: NfParamConfiguration[];
  p2Params?: ActivityInstanceExtendedParameterWithActivity[];
  nfActivities: NfPlaybookActivity[];
  sampleAttributes: Partial<IConfiguredAttribute>[];
  statusAfterSubmission: number;
  statusWhileRunning?: number;
  statusAfterCompletion?: number;
  statusAfterError?: number;
  reportTemplateResources?: File[];
  workflowReadsAttributeId?: number;
  p2ContextParams?: IP2ContextParams;
  // ...... other form fields
}

/**
 * Reducers
 */
export default createReducer(initialState, {
  [LOAD_ADMIN_PLAYBOOKS_REQUEST]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, isLoadingAdminPlaybooks: true };
  },
  [LOAD_ADMIN_PLAYBOOKS_SUCCESS]: (state: IPlaybookAdminState, payload: Playbook[]): IPlaybookAdminState => {
    return { ...state, isLoadingAdminPlaybooks: false, adminPlaybooks: payload };
  },
  [LOAD_ADMIN_PLAYBOOKS_FAILURE]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, isLoadingAdminPlaybooks: false, adminPlaybooks: [] };
  },

  [LOAD_ADMIN_PLAYBOOK_CATEGORIES_REQUEST]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, isLoadingPlaybookCategories: true };
  },
  [LOAD_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS]: (
    state: IPlaybookAdminState,
    payload: PlaybookCategory[],
  ): IPlaybookAdminState => {
    return { ...state, isLoadingPlaybookCategories: false, adminPlaybookCategories: payload };
  },
  [LOAD_ADMIN_PLAYBOOK_CATEGORIES_FAILURE]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, isLoadingPlaybookCategories: false, adminPlaybookCategories: [] };
  },

  [SAVE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS]: (
    state: IPlaybookAdminState,
    payload: PlaybookCategory,
  ): IPlaybookAdminState => {
    return { ...state, adminPlaybookCategories: [...state.adminPlaybookCategories, payload] };
  },
  [EDIT_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS]: (
    state: IPlaybookAdminState,
    payload: PlaybookCategory,
  ): IPlaybookAdminState => {
    return {
      ...state,
      adminPlaybookCategories: state.adminPlaybookCategories.map((category) => {
        if (category.id === payload.id) {
          return { ...category, name: payload.name, imageName: payload.imageName };
        }
        return category;
      }),
    };
  },
  [DELETE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS]: (
    state: IPlaybookAdminState,
    deletedCategories: PlaybookCategory[],
  ): IPlaybookAdminState => {
    const newCategoriesList = differenceBy(state.adminPlaybookCategories, deletedCategories, "id");
    return { ...state, adminPlaybookCategories: newCategoriesList };
  },

  [UPDATE_PLAYBOOK_CONFIG_STATE]: (
    state: IPlaybookAdminState,
    payload: Partial<IPlaybookConfigState>,
  ): IPlaybookAdminState => {
    return { ...state, playbookConfigState: { ...state.playbookConfigState, ...payload } };
  },
  [CLEAR_PLAYBOOK_CONFIG_STATE]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, playbookConfigState: {} };
  },
  [SAVE_PLAYBOOK_SUCCESS]: (state: IPlaybookAdminState): IPlaybookAdminState => {
    return { ...state, playbookConfigState: {} };
  },
});

/**
 * Action creators
 */

export function loadAdminPlaybooks() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Playbook[]> => {
    dispatch({ type: LOAD_ADMIN_PLAYBOOKS_REQUEST });

    return webApiClient
      .fetchAdminPlaybooks()
      .then((playbooks: Playbook[]) => {
        dispatch({
          type: LOAD_ADMIN_PLAYBOOKS_SUCCESS,
          payload: playbooks,
        });

        return playbooks;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_PLAYBOOKS_FAILURE,
          alertTitle: "Fetching of List of Playbooks Failed",
        }),
      );
  };
}

export function loadAdminPlaybookCategories(errorHandlerProps: Partial<ErrorHandlerProps>) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<PlaybookCategory[]> => {
    dispatch({ type: LOAD_ADMIN_PLAYBOOK_CATEGORIES_REQUEST });

    return webApiClient
      .fetchAdminPlaybookCategories()
      .then((playbookCategories: PlaybookCategory[]) => {
        dispatch({
          type: LOAD_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
          payload: playbookCategories,
        });
        return playbookCategories;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
          alertTitle: "Fetching of List of Playbook Categories Failed",
          ...errorHandlerProps,
        }),
      );
  };
}

export function fetchPlaybookCategoryById(categoryId: number) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<PlaybookCategory> => {
    dispatch({ type: LOAD_PLAYBOOK_CATEGORY_REQUEST });

    return webApiClient
      .fetchPlaybookCategoryById(categoryId)
      .then((playbookCategory: PlaybookCategory) => {
        dispatch({
          type: LOAD_PLAYBOOK_CATEGORY_SUCCESS,
          payload: playbookCategory,
        });
        return playbookCategory;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_PLAYBOOK_CATEGORY_FAILURE,
          alertTitle: "Fetching of List of Playbook Categories Failed",
        }),
      );
  };
}

export function saveAdminPlaybookCategory(playbookCategory: InputPlaybookCategoryInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<PlaybookCategory> => {
    dispatch({ type: SAVE_ADMIN_PLAYBOOK_CATEGORIES_REQUEST });

    return webApiClient
      .saveAdminPlaybookCategory(playbookCategory)
      .then((playbookCategory: PlaybookCategory) => {
        dispatch({
          type: SAVE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
          payload: playbookCategory,
        });

        return playbookCategory;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}

export function modifyAdminPlaybookCategory(playbookCategory: InputPlaybookCategoryInput) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<PlaybookCategory> => {
    dispatch({ type: EDIT_ADMIN_PLAYBOOK_CATEGORIES_REQUEST });

    return webApiClient
      .modifyAdminPlaybookCategory(playbookCategory)
      .then((playbookCategory: PlaybookCategory) => {
        notify.info({
          title: selMessage({ id: "admin.playbook.category.updated.successTitle" }),
          children: SelMessage({
            id: "admin.playbook.category.updated.successTitle",
            values: { categoryName: playbookCategory.name },
          }),
        });
        dispatch({
          type: EDIT_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
          payload: playbookCategory,
        });

        return playbookCategory;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: EDIT_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}

export function deleteAdminPlaybookCategory(playbookCategories: number[]) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<PlaybookCategory> => {
    dispatch({ type: DELETE_ADMIN_PLAYBOOK_CATEGORIES_REQUEST });

    return webApiClient
      .deleteAdminPlaybookCategory(playbookCategories)
      .then((deletedCategories: PlaybookCategory[]) => {
        notify.info({
          title: selMessage({
            id: "admin.playbook.category.deleted.successTitle",
            values: { categoryCount: deletedCategories.length },
          }),
          children: SelMessage({
            id: "admin.playbook.category.deleted.successMessage",
            values: { categoryCount: deletedCategories.length },
          }),
        });
        dispatch({
          type: DELETE_ADMIN_PLAYBOOK_CATEGORIES_SUCCESS,
          payload: deletedCategories,
        });
        return deletedCategories;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: DELETE_ADMIN_PLAYBOOK_CATEGORIES_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}

export function loadPlaybookForEditing(playbookId: number) {
  return (
    dispatch,
    _getState: () => IAppState,
    webApiClient: WebApiClient,
  ): Promise<ICombinedRegistrationNgsPlaybook> => {
    dispatch({ type: LOAD_ADMIN_PLAYBOOK_REQUEST });

    return webApiClient
      .fetchAdminPlaybook(playbookId)
      .then((playbook: Playbook) => {
        dispatch({
          type: LOAD_ADMIN_PLAYBOOK_SUCCESS,
        });
        return playbook;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_PLAYBOOK_FAILURE,
          alertTitle: `Loading of Playbook ${playbookId} Failed`,
        }),
      );
  };
}

export function updatePlaybookConfigState(state: Partial<IPlaybookConfigState>) {
  return {
    type: UPDATE_PLAYBOOK_CONFIG_STATE,
    payload: state,
  };
}

export function savePlaybook(progressCallback?: ProgressCallback) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<Playbook[]> => {
    const playbookConfigState: Partial<IPlaybookConfigState> = getState().playbookAdminState.playbookConfigState;

    const playbookPayload: InputPlaybookInput = createPlaybookConfigPayload(playbookConfigState);

    const filesToUpload: File[] = [];
    if (playbookConfigState.playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      // decide what we need to do with the template report resources
      [PLAYBOOK_REPORT_TEMPLATE_RESOURCES.DESIGN_FILE, PLAYBOOK_REPORT_TEMPLATE_RESOURCES.TEMPLATE_FILE].map(
        (type: PLAYBOOK_REPORT_TEMPLATE_RESOURCES) => {
          const file: File = playbookConfigState.reportTemplateResources.find(
            (resource) => resource.type === type.toLowerCase(),
          );
          const initialResource: ReportTemplateResources =
            playbookConfigState.initialPlaybook?.reportTemplateResources.find(
              (resource: ReportTemplateResources) => resource.type === type,
            );
          if (file?.size > 0) {
            // initial file is always of size 0, so that means the file has been updated
            filesToUpload.push(file);
          } else if (!file && initialResource) {
            // initial resource has been removed, that means we need to delete this resource
            filesToUpload.push(new File([], "foo", { type: type })); // empty file means we need to delete this resource
          }
        },
      );
    }

    dispatch({ type: SAVE_PLAYBOOK_REQUEST });

    return webApiClient
      .savePlaybook(playbookPayload)
      .then((playbook: Playbook) => {
        if (playbookConfigState.playbookType === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
          return Promise.all(
            filesToUpload.map((resource: File) =>
              dispatch(uploadPlaybookTemplateResource(playbook, resource, progressCallback)),
            ),
          ).then(() => playbook);
        }
        return playbook;
      })
      .then((playbook: Playbook) => {
        dispatch({
          type: SAVE_PLAYBOOK_SUCCESS,
          payload: playbook,
        });
        notify.info(
          {
            title: selMessage({ id: "admin.playbook.saved.successTitle" }),
            children: buildSavedPlaybookMessage(playbookPayload),
          },
          { autoClose: 10000 },
        );
        dispatch(loadAdminPlaybooks());

        return playbook;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: SAVE_PLAYBOOK_FAILURE,
          throwError: true,
          showAlert: false,
        }),
      );
  };
}

export function clearFormData() {
  return {
    type: CLEAR_PLAYBOOK_CONFIG_STATE,
  };
}

export function validatePlaybookExists(name: string, suffix: string) {
  return (_dispatch, _getState, webApiClient: WebApiClient): Promise<boolean> => {
    return webApiClient.validatePlaybookExists(name, suffix);
  };
}

export function downloadPlaybookTemplateResource(playbook: Playbook, resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .downloadFileAndWatchCompletion(`/playbookResourcesDownload/${playbook ? playbook.id : -1}/${resourceType}`, null)
      .catch(
        createErrorHandler(dispatch, {
          actionType: DOWNLOAD_FILE_FAILED,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function generatePlaybookTemplateResource(
  sampleTypeId: number,
  playbookTypeId: number,
  resourceType: PLAYBOOK_REPORT_TEMPLATE_RESOURCES,
) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    return webApiClient
      .downloadFileAndWatchCompletion(
        `/playbookResourcesGenerate/${sampleTypeId}/${playbookTypeId}/${resourceType}`,
        null,
      )
      .catch(
        createErrorHandler(dispatch, {
          actionType: DOWNLOAD_FILE_FAILED,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

function uploadPlaybookTemplateResource(playbook: Playbook, file: File, progressCallback?: ProgressCallback) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<any> => {
    const formData: FormData = new FormData();
    formData.append(file.name, file);

    return webApiClient
      .uploadFile(`/playbookResourcesUpload/${playbook.id}/${file.type}`, formData, progressCallback)
      .catch(
        createErrorHandler(dispatch, {
          actionType: UPLOAD_FILE_FAILED,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

export function initPlaybookConfig(playbookId: number) {
  return (dispatch, _getState: () => IAppState, _webApiClient: WebApiClient): Promise<Playbook> => {
    return dispatch(loadPlaybookForEditing(playbookId)).then(async (playbook: Playbook) => {
      const playbookConfigState: Partial<IPlaybookConfigState> = await initPlaybookConfigState(playbook);
      dispatch(updatePlaybookConfigState(playbookConfigState));
      return playbook;
    });
  };
}

export function fetchNfWorkflows() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<Workflow[]> => {
    dispatch({ type: LOAD_APPROVED_WORKFLOWS_REQUEST });

    return webApiClient.fetchNfWorkflows().catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_APPROVED_WORKFLOWS_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function fetchPublishedWorkflows() {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<WorkflowP2[]> => {
    dispatch({ type: LOAD_APPROVED_WORKFLOWS_REQUEST });

    return webApiClient.fetchPublishedWorkflows().catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_APPROVED_WORKFLOWS_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function fetchNfWorkflow(workflowName: string) {
  return (dispatch, _getState: () => IAppState, webApiClient: WebApiClient): Promise<NfWorkflow> => {
    dispatch({ type: LOAD_ADMIN_NFWORKFLOW_REQUEST });

    return webApiClient.fetchNfWorkflow(workflowName).catch(
      createErrorHandler(dispatch, {
        actionType: LOAD_ADMIN_NFWORKFLOW_FAILURE,
        showAlert: false,
        throwError: true,
      }),
    );
  };
}

export function fetchP2Workflow(workflowId: number) {
  return (dispatch, getState: () => IAppState, webApiClient: WebApiClient): Promise<WorkflowP2> => {
    dispatch({ type: LOAD_ADMIN_P2WORKFLOW_REQUEST });

    return webApiClient
      .fetchP2Workflow(workflowId)
      .then(async (workflow) => {
        // note that the playbook-specific p2Params may already be loaded and only need to be completed
        // (in case new params were added to the workflow)
        const playbookP2Params = getState().playbookAdminState.playbookConfigState.p2Params || [];
        // only use the workflow setting if there are no playbook-specific equivalents already present in the store
        const workflowP2Params: ActivityInstanceExtendedParameterWithActivity[] =
          await flattenStagedActivityExtendedInstances(workflow.extendedConfig.activities, true, true);
        // filter out all params already configured for the playbook (do not overwrite them or create duplicates)
        const newP2Params = workflowP2Params.filter(
          (wp) =>
            !playbookP2Params?.find(
              (pp) =>
                pp.activity.identifier === wp.activity.identifier &&
                pp.parameterDefinition.id === wp.parameterDefinition.id,
            ),
        );
        playbookP2Params.push(...newP2Params);

        dispatch(
          updatePlaybookConfigState({
            p2Params: playbookP2Params.sort((a, b) => ((a.sortOrder || 0) > (b.sortOrder || 0) ? 1 : -1)),
          }),
        );
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: LOAD_ADMIN_P2WORKFLOW_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

/********************** Utils ***************************/

export function buildConfiguredAttribute(a: ExperimentInput): Partial<IConfiguredAttribute> {
  return {
    id: a.trackerAttributeId || a.workflowParamId,
    isWorkflowParam: !!a.workflowParamId,
    description: a.description,
    label: a.label,
    correctedTypeId: a.correctedTypeId,
    correctedMandatory: a.correctedMandatory,
  };
}

export function buildConfiguredNfAttribute(
  a: NfSampleAttribute,
  mappings?: InputWorkflowParamAttributeMapping[],
): Partial<IConfiguredAttribute> {
  return {
    id: a.attributeId,
    isWorkflowParam: false,
    // make sure that attributes used in the mappings cannot be removed from the playbook by setting the "global" mandatoriness
    mandatory: mappings ? !!mappings.find((m) => m.attributeId === a.attributeId) : false,
    correctedMandatory: a.correctedMandatory,
  };
}

export async function initPlaybookConfigState(playbook: Playbook): Promise<Partial<IPlaybookConfigState>> {
  const playbookConfig: ICombinedRegistrationNgsPlaybookConfig =
    playbook.config as ICombinedRegistrationNgsPlaybookConfig;
  const workflowId =
    playbookConfig?.ngsWorkflow?.workflowId || playbook.nfConfig?.workflowName || playbook.p2Config?.workflow?.id;
  const reportTemplateResources: File[] = playbook.reportTemplateResources.map(
    (resource: ReportTemplateResources) => new File([], resource.name),
  );
  const nfAnalysisTrackerImportMappings: InputWorkflowParamAttributeMapping[] = [];
  if (playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES) {
    playbook.nfConfig?.trackerParameters?.sampleAttributes?.forEach((a) => {
      if (a?.workflowParam) {
        nfAnalysisTrackerImportMappings.push({
          attributeId: a.attributeId,
          workflowFieldId: a.workflowParam,
        });
      }
    });
  }

  const genomeContextParams = playbook.p2Config?.contexts.find((c) => c?.context === UiContextEnum.GenomeViewer);

  const activities = playbook.p2Config?.workflow?.extendedConfig.activities;
  const p2Params: ActivityInstanceExtendedParameterWithActivity[] = activities
    ? await flattenStagedActivityExtendedInstances(
        activities,
        // do not filter on staged here because only the ones that are staged on workflow level are retrieved anyway
        false,
        true,
      )
    : [];

  const playbookFormState: IPlaybookConfigState = {
    initialPlaybook: playbook,
    id: playbook.id,
    name: playbook.name,
    nameSuffix: playbook.nameSuffix,
    description: playbook.description,
    icon: getIconElementForCustomPlaybook(playbook),
    playbookType: playbook.type?.id,
    playbookCategories: playbook.categories,
    sampleTypeId: playbook.sampleType?.id,
    workflowId: workflowId,
    parentAttributes: playbookConfig?.experimentInput
      ?.filter((attribute) => attribute.trackerAttributeId)
      .map(buildConfiguredAttribute),
    workflowAttributes: playbookConfig?.experimentInput
      ?.filter((attribute) => attribute.workflowParamId)
      .map(buildConfiguredAttribute),
    sampleAttributes:
      playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES
        ? playbook.nfConfig.trackerParameters.sampleAttributes.map((a) =>
            buildConfiguredNfAttribute(a, nfAnalysisTrackerImportMappings),
          )
        : playbookConfig?.sampleInput?.map(buildConfiguredAttribute),
    //ngsDataProcedure: playbookConfig.ngsWorkflow?.ngsDataProcedure,
    statusAfterSubmission: playbookConfig?.sampleStatus.afterSubmission,
    statusWhileRunning: playbookConfig?.sampleStatus.whileRunning,
    statusAfterCompletion: playbookConfig?.sampleStatus.afterCompletion,
    statusAfterError: playbookConfig?.sampleStatus.failure,
    workflowExportMappings: playbookConfig?.ngsWorkflow?.workflowExportMappings,
    workflowImportMappings:
      playbook.type.id === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES
        ? nfAnalysisTrackerImportMappings
        : playbookConfig?.ngsWorkflow?.workflowImportMappings,
    reportTemplateResources: reportTemplateResources,
    nfParams: playbook.nfConfig?.workflowParameters,
    p2Params: p2Params,
    nfActivities: playbook.nfConfig?.activities,
    workflowReadsAttributeId: playbook.nfConfig?.trackerParameters?.sampleAttributes?.find(
      (configuredAttribute) => configuredAttribute.workflowParam,
    )?.attributeId,
    uiContexts: playbook.uiContexts,
    p2ContextParams: genomeContextParams
      ? {
          genomeParam: {
            activity: { identifier: (genomeContextParams as GenomeViewerUiContext).genomeParam.instanceId },
            parameterDefinition: { id: (genomeContextParams as GenomeViewerUiContext).genomeParam.parameterId },
          } as ActivityInstanceExtendedParameterWithActivity,
          regionParam: {
            activity: { identifier: (genomeContextParams as GenomeViewerUiContext).regionParam.instanceId },
            parameterDefinition: { id: (genomeContextParams as GenomeViewerUiContext).regionParam.parameterId },
          } as ActivityInstanceExtendedParameterWithActivity,
        }
      : null,
  };
  return playbookFormState;
}

function createSampleRegistrationPlaybookConfigurationPayload(
  playbookConfigState: Partial<IPlaybookConfigState>,
): ISampleRegistrationPlaybookConfig {
  const sampleParentAttributesInput: InputExperimentInput[] =
    playbookConfigState.parentAttributes?.map((attribute: IConfiguredAttribute) => ({
      trackerAttributeId: attribute.id,
      // Tracker attribute defaults are stored relationally
      description: attribute.description,
      label: attribute.label,
      correctedMandatory: attribute.correctedMandatory,
    })) ?? [];

  const workflowAttributesInput: InputExperimentInput[] =
    playbookConfigState.workflowAttributes?.map(
      (attribute: IConfiguredAttribute) =>
        ({
          workflowParamId: attribute.id,
          workflowParamDefault: null, // TODO ? currently the default from the Workflow are provided, no way to configure additional defaults
          description: attribute.description,
          label: attribute.label,
          correctedTypeId: attribute.correctedTypeId,
        }) satisfies InputExperimentInput,
    ) ?? [];

  const playbookConfig: ISampleRegistrationPlaybookConfig = {
    experimentInput: sampleParentAttributesInput.concat(workflowAttributesInput),
    sampleInput:
      playbookConfigState.sampleAttributes?.map((attribute: IConfiguredAttribute) => ({
        trackerAttributeId: attribute.id,
        description: attribute.description,
        label: attribute.label,
        correctedMandatory: attribute.correctedMandatory,
      })) ?? [],
    sampleStatus: {
      afterSubmission: playbookConfigState.statusAfterSubmission,
      whileRunning: playbookConfigState.statusWhileRunning,
      afterCompletion: playbookConfigState.statusAfterCompletion,
      failure: playbookConfigState.statusAfterError,
    },
    icon: playbookConfigState.icon.id,
  };
  return playbookConfig;
}

function createNfSampleRegistrationPlaybookConfigurationPayload(
  playbookConfigState: Partial<IPlaybookConfigState>,
): InputNfTrackerParamInput {
  const paramAttributeMappings = playbookConfigState.workflowImportMappings;

  const playbookTrackerConfig: InputNfTrackerParamInput = {
    sampleAttributes:
      playbookConfigState.sampleAttributes?.map((attribute: IConfiguredAttribute) => ({
        attributeId: attribute.id,
        correctedMandatory: attribute.correctedMandatory,
        workflowParam: paramAttributeMappings.find((mapping) => attribute.id === mapping.attributeId)?.workflowFieldId,
      })) ?? [],
  };

  return playbookTrackerConfig;
}

export function createPlaybookConfigPayload(playbookConfigState: Partial<IPlaybookConfigState>): InputPlaybookInput {
  // construct the configuration payload according to the Playbook Type
  let playbookConfigPayload: ISampleRegistrationPlaybookConfig | ICombinedRegistrationNgsPlaybookConfig = null;
  let playbookNfConfigPayload: InputNfPlaybookConfigInput = null;
  let playbookP2ConfigPayload: InputP2PlaybookConfigInput = null;
  let p2UiContexts: InputPlaybookParametersUiWrapperInput[] = [];

  switch (playbookConfigState.playbookType) {
    case PLAYBOOK_TYPE.SAMPLE_REGISTRATION:
      playbookConfigPayload = createSampleRegistrationPlaybookConfigurationPayload(playbookConfigState);
      break;
    case PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES:
    case PLAYBOOK_TYPE.NGS_ANALYSIS:
    case PLAYBOOK_TYPE.RANGE_ANALYSIS:
      playbookConfigPayload = {
        ...createSampleRegistrationPlaybookConfigurationPayload(playbookConfigState),
        ngsWorkflow: {
          //ngsDataProcedure: playbookConfigState.ngsDataProcedure,
          workflowId: playbookConfigState.workflowId,
          workflowExportMappings: playbookConfigState.workflowExportMappings,
          workflowImportMappings: playbookConfigState.workflowImportMappings,
        },
      };
      break;
    case PLAYBOOK_TYPE.PROCESSOR:
      p2UiContexts = playbookConfigState.uiContexts?.map((c) => {
        return c === UiContextEnum.GenomeViewer
          ? {
              context: c,
              genomeParam: {
                instanceId: playbookConfigState.p2ContextParams?.genomeParam?.activity?.identifier,
                parameterId: playbookConfigState.p2ContextParams?.genomeParam?.parameterDefinition.id,
              },
              genomicRegionParam: {
                instanceId: playbookConfigState.p2ContextParams?.regionParam.activity.identifier,
                parameterId: playbookConfigState.p2ContextParams?.regionParam?.parameterDefinition.id,
              },
            }
          : { context: c };
      });

      playbookP2ConfigPayload = {
        icon: playbookConfigState.icon?.id,
        workflowId: Number(playbookConfigState.workflowId),
        contexts: p2UiContexts,
        activitiesConfig: createP2ActivitiesConfig(playbookConfigState),
      };

      break;
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS:
    case PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES:
    case PLAYBOOK_TYPE.REPORT_FROM_VARIANTS:
    case PLAYBOOK_TYPE.REPORT_FROM_COORDINATES:
      playbookNfConfigPayload = {
        icon: playbookConfigState.icon?.id,
        workflowName: playbookConfigState.workflowId,
        activities: playbookConfigState.nfActivities?.map((configuredActivity) => {
          const nfParamInput: InputNfPlaybookActivity = {
            ...configuredActivity,
          };
          return nfParamInput;
        }),
        trackerParameters:
          playbookConfigState.playbookType === PLAYBOOK_TYPE.NEXTFLOW_ANALYSIS_WITH_REGISTERED_SAMPLES
            ? createNfSampleRegistrationPlaybookConfigurationPayload(playbookConfigState)
            : null,
        profiles: playbookConfigState.nfWorkflow.profiles,
        workflowParameters: playbookConfigState.nfParams.map(
          ({ id: _id, defaultWorkflowValue: _default, values, ...nfParamProps }) => {
            const nfParamInput: InputNfParamInput = {
              ...nfParamProps,
              values:
                values?.map((value) => {
                  const nfParamValue: InputNfParamValueInput = {
                    genome: value.organism && {
                      genomeId: value.organism?.id,
                      fileId: null,
                    },
                    text: value.textValue,
                    numberValue: value.numberValue,
                    booleanValue: value.booleanValue,
                    cfiId: value.file?.id,
                    region: value.region && {
                      range: value.region.range,
                      contigId: value.region.contig.id,
                    },
                  } satisfies InputNfParamValueInput;
                  return nfParamValue;
                }) ?? [],
            };
            return nfParamInput;
          },
        ),
      };
      break;
  }

  const allTrackerAttributes: Partial<IConfiguredAttribute>[] = (playbookConfigState.sampleAttributes ?? []).concat(
    playbookConfigState.parentAttributes ?? [],
  );

  // will actually depend on the chosen playbook type when we support multiple playbook types
  const playbookPayload: InputPlaybookInput = {
    playbookId: playbookConfigState.id, // case of editing
    name: playbookConfigState.name,
    nameSuffix: playbookConfigState.nameSuffix,
    description: playbookConfigState.description,
    playbookCategoryIds: playbookConfigState.playbookCategories.map((category) => category.id),
    playbookType: playbookConfigState.playbookType,
    sampleType: playbookConfigState.sampleTypeId,
    helpLink: "NGS%2bSample%2bProcessing%2bPlaybook", // mandatory as DB constraint, this constraint needs to go when we add help link input field !
    tooltip: `Execute Playbook '${playbookConfigState.name}'`,
    playbookAttributes: allTrackerAttributes.map((attribute: IConfiguredAttribute) => ({
      // attributeDefault: {...attribute.attributeDefault, defaultVocabularyTermId: attribute.attributeDefault?.defaultTerm?.id}, // TODO
      attributeId: attribute.id,
      attributeDefault: null,
    })),
    config: playbookConfigPayload,
    nfConfig: playbookNfConfigPayload,
    p2Config: playbookP2ConfigPayload,
  };
  return playbookPayload;
}
function createP2ActivitiesConfig(
  playbookConfigState: Partial<IPlaybookConfigState>,
): InputActivityExtendedInstanceInput[] {
  const activitiesConfig: InputActivityExtendedInstanceInput[] = [];
  const p2Params: ActivityInstanceExtendedParameterWithActivity[] | undefined = playbookConfigState.p2Params;

  // first assign sort order to all parameters, based on the current sorting
  const sortedP2Params = p2Params?.map((p, idx) => {
    return { ...p, sortOrder: idx };
  });

  // get all activity IDs of the list
  const activityIds: string[] = uniq(p2Params?.map((p) => p.activity.identifier));

  activityIds.forEach((activityId) => {
    const parameters: ActivityInstanceExtendedParameterWithActivity[] = sortedP2Params?.filter(
      (p) => p.activity.identifier === activityId,
    );
    const parameterInputs: InputActivityExtendedParameterInput[] = parameters.map((p) => {
      return {
        description: p.parameterDefinition.description,
        identifier: p.parameterDefinition.id,
        label: p.parameterDefinition.label,
        sortOrder: p.sortOrder,
        staged: p.staged,
        optional: p.optional,
        values: extractValue(p.workflowParameter),
      };
    });
    activitiesConfig.push({ identifier: activityId, parameters: parameterInputs });
  });

  return activitiesConfig;
}

async function buildWorkflowParameterFromExtendedParameter(
  param: ActivityInstanceExtendedParameterWithActivity,
  treatParamsAsOptional: boolean,
  sampleIds?: number[],
): Promise<WorkflowParameter> {
  const fieldProps: WorkflowActivityParameterFieldProps = buildFieldProps(param, treatParamsAsOptional);
  let value = await getValueFromActivityInstanceExtendedParameterWithActivity(param);

  // special logic if the playbook was launched from a sample selection (passed as sampleIds):
  // these sampleIds overrule al other sample IDs, e.g. from defaults
  if (param.parameterDefinition.__typename === "SampleParam" && sampleIds) {
    const extractedValue = value.extractNullable() as InputSampleValueInput;

    const maybeValue = {
      attributeId: extractedValue?.attributeId,
      sampleFileMappings:
        sampleIds.map((s) => ({
          sampleId: s,
          fileIds: [],
        })) ?? [],
    };

    value = Just(maybeValue);
  }

  return buildWorkflowParameter(
    param.activity.identifier,
    param.parameterDefinition.id,
    param.parameterDefinition.__typename,
    value,
    param.staged,
    generateValidationSchema(fieldProps),
  );
}

export function buildWorkflowParameter(
  nodeId: string,
  parameterId: string,
  typeName: any,
  values: Maybe<AllParametersTypes>,
  staged: boolean,
  schema: ZodType<AllParametersTypes, ZodTypeDef, AllParametersTypes> | undefined,
) {
  const workflowParameter: WorkflowParameter = {
    nodeId,
    parameterId,
    typeName,
    values,
    staged,
    schema,
  };
  return workflowParameter;
}

export function buildFieldProps(
  p2Param: ActivityInstanceExtendedParameterWithActivity,
  treatParamsAsOptional?: boolean,
): WorkflowActivityParameterFieldProps {
  // clone, otherwise we would potentially modify the original optional setting
  const parameterDefinition: ActivityParameter = { ...p2Param.parameterDefinition };

  if (treatParamsAsOptional) {
    // in the playbook editor we do not want mandatoriness to be enforced
    parameterDefinition.optional = true;
  } else {
    // set the customized value for optional
    parameterDefinition.optional = p2Param.optional;
  }

  return {
    ...parameterDefinition,
    nodeId: p2Param.activity.identifier,
  } as WorkflowActivityParameterFieldProps;
}

export async function flattenStagedActivityExtendedInstances(
  activities: ActivityExtendedInstance[],
  filterStaged: boolean,
  treatParamsAsOptional: boolean,
  sampleIds?: number[],
): Promise<ActivityInstanceExtendedParameterWithActivity[]> {
  const p2Params: ActivityInstanceExtendedParameterWithActivity[] = [];
  for (const a of activities) {
    const filteredParams = a.parameters.filter((p) => p.staged || !filterStaged);
    for (const p of filteredParams) {
      const flattenedParam = await flattenActivityInstanceExtendedParameter(a, p, treatParamsAsOptional, sampleIds);
      p2Params.push(flattenedParam);
    }
  }
  return p2Params;
}

async function flattenActivityInstanceExtendedParameter(
  activity: ActivityExtendedInstance,
  parameter: ActivityInstanceExtendedParameter,
  treatParamsAsOptional: boolean,
  sampleIds?: number[],
): Promise<ActivityInstanceExtendedParameterWithActivity> {
  const newParameter: ActivityInstanceExtendedParameterWithActivity = {
    ...parameter,
    // Make sure the ID is unique by extending it with the activity identifier in order to avoid issues in the SortableContext.
    // This ID should never be used explicitly, except for the SortableContext and potential key usages.
    // In all other cases the parameterDefinition ID should be used instead.
    id: `${activity.identifier}-${parameter.parameterDefinition.id}`,
    activity: activity,
  };
  // Add the worflowParameter for usage in the default handling.
  // Values will be taken from it when creating the playbook config payload
  newParameter.workflowParameter = await buildWorkflowParameterFromExtendedParameter(
    newParameter,
    treatParamsAsOptional,
    sampleIds,
  );
  return newParameter;
}

// this is a simplified equivalent of activity.ts - getValueFromWorkflowParameter
async function getValueFromActivityInstanceExtendedParameterWithActivity(
  parameter: ActivityInstanceExtendedParameterWithActivity,
): Promise<Maybe<AllParametersTypes>> {
  return (
    match(parameter.parameterDefinition)
      .with({ type: WorkflowParameterTypeEnum.Text }, () => {
        //  __typename: "StringParam"
        const value = (parameter?.values as StringParameterValueFragment)?.stringValue;

        return value ? Just(value) : Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.Number }, () => {
        // __typename: "NumberParam"
        const value = (parameter?.values as NumberParameterValueFragment)?.numberValue;
        return value ? Just(value) : Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.Boolean }, () => {
        // __typename: "BooleanParam"
        const value = (parameter?.values as BooleanParameterValueFragment)?.booleanValue;
        return Just(value ?? false);
      })
      .with({ type: WorkflowParameterTypeEnum.Genome }, () => {
        // __typename: "GenomeParam"
        /* No default value for Genome */
        const genomeValue = (parameter?.values as GenomeParameterValueFragment)?.genomeValue;
        const value = genomeValue?.hidden ? -1 : genomeValue?.id; // make sure inaccessible resources have an invalid id
        return value ? Just(value) : Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.File }, () => {
        // __typename: "FileParam"
        /* No default value for File */
        // Remove null values (wrong fileId not in the DB)
        const value = (parameter?.values as FileParameterValueFragment)?.fileValue
          .map((val) => (val?.hidden ? -1 : val?.deleted ? -2 : val?.id)) // make sure inaccessible resources have an invalid id
          .filter<number>((val): val is number => typeof val === "number");

        return value?.length ? Just(value) : Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.Sample }, async () => {
        // __typename: "SampleParam"
        /* No default value for File */
        const values = parameter?.values as SampleParameterValueFragment;

        if (values?.sampleValue && values?.attribute?.id != undefined) {
          const maybeValue = {
            attributeId: values.attribute.id,
            // The file association may have changed. So we reload the files!
            // This is the reason for the async function from here up to initPlaybookConfigState...
            sampleFileMappings: await getSampleFileMappings(
              values.attribute.id,
              values.sampleValue
                .filter((value) => value != undefined)
                .map((value) => (value?.sample?.hidden ? -1 : value?.sample?.deleted ? -2 : value!.sample?.id)),
            ),
          };

          return Just(maybeValue);
        }

        return Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.GenomicRegion }, () => {
        // __typename: "RegionParam"
        /* No default value for Region */

        // Parse value
        let value: RegionParamType | null = null;
        const regionValue = (parameter?.values as RegionParameterValueFragment)?.regionValue;
        if (regionValue?.contig) {
          value = { contigId: regionValue.contig.id, range: regionValue.range };
        }

        return value ? Just(value) : Nothing;
      })
      .with({ type: WorkflowParameterTypeEnum.Date }, () => {
        // __typename: "DateParam"
        /* DO NOTHING */
        return Nothing;
      })
      // __typename: "Tracker" should not be relevant
      .exhaustive()
  );
}
